import { Box } from '@mui/material';
import { vec3 } from 'gl-matrix';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { useFrameRotationVersion } from '~/hooks/environment/useEnvironmentViews';
import GhostLineSample from '~/components/Viewer/GhostLineSample';
import {
    GHOST_SIDES,
    GhostDepth,
    GhostSide,
    ghostSideTowards,
} from '~/engine/core/render/renderers/slices/adjacentSliceGhost';
import type { PlaneView } from '~/engine/view/views/PlaneView';
import { useShownAdjacentSliceGhosts } from '~/hooks/viewer/useAdjacentSliceGhosts';
import {
    selectedRoiReadModelState,
    structureColorEditOfState,
} from '~/state/atoms/structureSet/roiAtoms';
import { dicomColorToRgbString } from '~/utils/color';
import { DirectionLabel, directionLabel } from '~/utils/orientation';
import { OverlayLabel, OverlayRow } from '~/components/OverlayLabel';
import { ABOVE_TILE_CORNER_BOTTOM } from '~/viewportGrid/chrome';

/**
 * Legend for the adjacent-slice contour ghosts of one view, at the bottom
 * left of its tile: which line pattern is which side of the displayed plane,
 * by the view's own edge letters, so they agree with the edges and follow a
 * turn of its frame; and how many slices away a side's ghosts are when that
 * is more than the neighbour. Shown in the selected structure's colour while
 * the ghosts are shown ({@link useShownAdjacentSliceGhosts}), the selected
 * structure belongs to the structure set the view draws, it is drawn and
 * some slice is chosen; a side with none chosen has no entry.
 */
export default function AdjacentSliceGhostLegend({
    view,
}: {
    view: PlaneView;
}) {
    const intl = useIntl();
    const depths = useShownAdjacentSliceGhosts();
    const roi = useRecoilValue(selectedRoiReadModelState);
    const colorEdit = useRecoilValue(
        structureColorEditOfState(roi?.roiNumber ?? -1)
    )?.pending;
    const environment = view.environment;
    useFrameRotationVersion(environment);
    const shownSides = depths
        ? GHOST_SIDES.filter((side) => depths[side].length > 0)
        : [];

    if (
        !depths ||
        shownSides.length === 0 ||
        !roi ||
        environment.structureSet?.sopInstanceUID !== roi.structureSetUid ||
        roi.isPointRoi ||
        !roi.visible
    ) {
        return null;
    }

    const viewOrientation = view.getOrientation();
    // Column 2 of the view → patient matrix is the view normal.
    const viewNormal = vec3.fromValues(
        viewOrientation[6],
        viewOrientation[7],
        viewOrientation[8]
    );
    const viewNormalSide = ghostSideTowards(viewNormal);
    /** The edge letter of the direction `side` lies in, for this view. */
    const labelOf = (side: GhostSide): DirectionLabel =>
        directionLabel(
            side === viewNormalSide
                ? viewNormal
                : vec3.negate(vec3.create(), viewNormal)
        );
    const depthLabelsOf = (sideDepths: ReadonlyArray<GhostDepth>) => {
        if (sideDepths.length === 1 && sideDepths[0] === 1) {
            return '';
        }
        return sideDepths
            .map((depth) =>
                intl.formatMessage(
                    { id: 'toolbar.adjacentSliceGhosts.depth' },
                    { depth }
                )
            )
            .join(', ');
    };
    const accessibleSides = shownSides
        .map((side) => {
            const depthLabels = depthLabelsOf(depths[side]);
            return intl.formatMessage(
                {
                    id: depthLabels
                        ? `structure.adjacentSliceGhosts.legend.${side}.withDepths`
                        : `structure.adjacentSliceGhosts.legend.${side}`,
                },
                { direction: labelOf(side), depths: depthLabels }
            );
        })
        .join('; ');
    // An open colour edit previews here as it does on the ghosts.
    const color = dicomColorToRgbString(colorEdit ?? roi.color);

    return (
        <OverlayRow
            role="img"
            aria-label={intl.formatMessage(
                { id: 'structure.adjacentSliceGhosts.legend' },
                { sides: accessibleSides }
            )}
            data-testid="adjacent-slice-ghost-legend"
            sx={{
                position: 'absolute',
                left: 6,
                bottom: ABOVE_TILE_CORNER_BOTTOM,
                pointerEvents: 'none',
            }}
        >
            {shownSides.map((side) => (
                <LegendEntry
                    key={side}
                    side={side}
                    color={color}
                    label={labelOf(side)}
                    depthLabels={depthLabelsOf(depths[side])}
                />
            ))}
        </OverlayRow>
    );
}

/** One side's line sample and letter, then its distances unless it shows only the neighbour. */
function LegendEntry({
    side,
    color,
    label,
    depthLabels,
}: {
    side: GhostSide;
    color: string;
    label: DirectionLabel;
    depthLabels: string;
}) {
    return (
        <OverlayLabel
            data-testid={`adjacent-slice-ghost-legend-${side}`}
            sx={{ gap: 0.5 }}
        >
            <GhostLineSample side={side} color={color} />
            {label}
            {depthLabels && (
                <Box
                    component="span"
                    sx={{ opacity: 0.7 }}
                    data-testid={`adjacent-slice-ghost-legend-${side}-depths`}
                >
                    {depthLabels}
                </Box>
            )}
        </OverlayLabel>
    );
}
